// LG WebOS Cast-/OTT-Integration ueber die HCAP-JavaScript-API (Pro:Centric).
//
// Belegt mit der offiziellen LG-Partnerdoku (procentric.developer.lge.com,
// HCAP for JavaScript v1.24). Methodennamen + "since"-Versionen in Kommentaren.
//
// WICHTIGE Unterschiede zur Samsung-Tizen-Loesung (helpers/Cast/Tizen.js):
//  - LG hat KEIN webapis.googlecast-Pendant. Das eigentliche Cast-/Pairing-
//    Erlebnis liefert LGs eingebautes Google Cast / AirPlay (ab webOS 23) bzw.
//    Screen Share (Miracast); programmatisch ansteuerbar ist davon nur die
//    jeweilige Einstiegs-App.
//  - SoftAP-SSID/-Passwort gibt es NICHT in hcap.network.setSoftAP, aber sehr
//    wohl als Properties: SSID = "tv_name", Passwort = "soft_ap_password"
//    (LG-Support-Auskunft 08/2026). Ein Samsung-artiger WLAN-QR ist damit auch
//    auf LG baubar - siehe configureSoftAP().
//  - Firmware-Update laeuft ueber den Pro:Centric-Server (startManualUpdate),
//    nicht ueber eine frei uebergebene .bem-URL.
//  - Cloning ist XML-Cloning (requestCloning{xml}), kein .sclone-Dateipfad.
//  - "Clear Credentials" + Snapshot-Restore sind in der Checkout-API gebuendelt.
//
// Premium-OTT (Netflix, Prime Video, Disney+, Paramount+) sind NICHT Teil des
// CP-App-Katalogs der Hospitality-Firmware; welche CP-Apps ein Geraet kennt,
// haengt an Modell und Region und steht in application.getApplicationList().
// Fuer Netflix (und die uebrigen OTT-Apps) ist ein Vertrags-/Lizenzprozess ueber
// den lokalen LG-Sales-Engineer noetig, inkl. app-spezifischer Tokens - ohne den
// laesst sich die App per API nicht nachinstallieren (LG-Auskunft 08/2026).
//
// AUSBLICK: LG empfiehlt, statt hcap.js kuenftig idcap.js zu verwenden - HCAP
// soll perspektivisch eingestellt werden. Diese Datei ist noch reines HCAP.
//
// hcap wird von der LG-Firmware global bereitgestellt (siehe helpers/DeviceType).

import DeviceType from '@/helpers/DeviceType';
import Idcap from './Idcap';
import { findAppMatch } from './appMatch';

// Native Screen-Share-(Miracast-)App. Auf Hospitality-Firmware heisst sie
// com.webos.app.commercial.miracastguide - belegt durch getApplicationList()
// auf einem 43UK660H. Die generische Consumer-Id bleibt als Fallback.
export const LG_SCREEN_SHARE_APP_IDS = [
  'com.webos.app.commercial.miracastguide',
  'com.webos.app.miracast',
];
export const LG_SCREEN_SHARE_APP_ID = LG_SCREEN_SHARE_APP_IDS[0];

// Native Google-Cast-Einstiege. BELEGT auf einem 43UK660H (Hospitality, 2025,
// webOS 25) ueber getApplicationList():
//   com.webos.app.commercial.chromecastguide -> "Google Cast Guide"
//   com.webos.chromecast-settings            -> "Google Cast Settings"
// Der Guide ist der Pairing-Screen, den der TV dem Gast selbst anzeigt - genau
// das, was die Strategie 'lg-native-cast' ansteuert.
//
// Ab wann es diese Apps gibt, ist NICHT vermessen: bekannt ist nur, dass sie auf
// webOS 25 vorhanden sind. Deshalb entscheidet die Geraete-Abfrage und nicht
// eine angenommene Versionsgrenze.
export const LG_GOOGLE_CAST_APP_IDS = [
  'com.webos.app.commercial.chromecastguide',
  'com.webos.chromecast-settings',
];

// AirPlay-Einstieg. Auf dem Testgeraet nicht vorhanden; die Id folgt dem
// Namensmuster der commercial-Guides und ist NICHT belegt. Da nur gestartet
// wird, was getApplicationList() meldet, ist ein falscher Eintrag harmlos.
export const LG_AIRPLAY_APP_IDS = [
  'com.webos.app.commercial.airplayguide',
  'com.webos.app.airplay',
];

// Kandidaten fuer den nativen Cast-/Pairing-Einstieg, in Wunsch-Reihenfolge.
// HCAP 1.24 hat KEINE Cast-API (kein Pendant zu webapis.googlecast) - der
// einzige programmatische Weg ist launchApplication mit der App-Id des nativen
// Einstiegs. Die Liste wird zur Laufzeit gegen getApplicationList() geprueft;
// gestartet wird nur, was das Geraet auch meldet. Falsche Ids sind dadurch
// harmlos - sie matchen einfach nicht.
//
// Screen Share ist hier bewusst NICHT enthalten: Bildschirmspiegelung ist kein
// Cast und die App gibt es auch auf sehr alten Geraeten.
export const LG_CAST_ENTRY_CANDIDATES = LG_GOOGLE_CAST_APP_IDS.concat(LG_AIRPLAY_APP_IDS);

// Evidenz schlaegt Versionsgrenze: meldet das Geraet einen Google-Cast-Einstieg,
// gilt Cast als unterstuetzt - auch wenn die Versionserkennung nichts Brauchbares
// liefert. Das ist auf Hospitality der Normalfall, weil dort keine Property die
// webOS-Generation herausgibt.
// Auf true setzen, wenn ausschliesslich die Versionsgrenze gelten soll.
export const LG_CAST_STRICT_VERSION_GATE = false;

// Property-Keys, die fuer Casting relevant sind bzw. es sein koennten.
// Nicht alle existieren auf jeder Firmware - getProperty liefert dann null,
// und die Diagnose auf der Info-Seite zeigt genau, welche das Geraet kennt.
export const LG_CAST_PROPERTY_KEYS = [
  // Belegt: die Firmware feuert dafuer ein property_changed-Event.
  'chromecast_data_registration',
  'dial',
  'wifi_screen_share',
  'asl_mode',
  'screen_share',
  'airplay',
  'google_cast',
  'cast_mode',
  'soft_ap',
  'soft_ap_ui',
  // SSID des SoftAP. LG fuehrt sie als TV-Name - dieselbe Property, die auch
  // den Geraetenamen in Cast-/AirPlay-Geraetelisten bestimmt.
  'tv_name',
  // Das Passwort. Laut HCAP-Property-Liste eine normale Read/Write-Property
  // (in IDCAP steht ausdruecklich: "changed to Read/Write from Write Only in
  // IDPN304"). Auf aelteren Firmwares antwortet getProperty nicht - deshalb
  // wird der Wert weiterhin selbst gemerkt, ein lesbarer Wert aber bevorzugt.
  'soft_ap_password',
  // 0 = SSID des SoftAP wird versteckt (seit IDPN 200). Steht das auf 1, findet
  // das Handy das Netz beim Scannen nicht - der QR muesste dann H:true tragen.
  'softap_ssid_hidden',
  // 0 (Default!) = "QR code and broadcast SSID are disabled". Wir setzen die
  // Property nicht, weil damit auch LGs eigener Pairing-QR erscheint; sie steht
  // hier, damit auf der Diagnose-Seite sichtbar ist, wie das Geraet konfiguriert
  // ist, wenn ein Handy das Netz nicht findet.
  'smart_pairing',
  // Die Standby-LED. Aufgefallen, weil die Firmware von sich aus ein
  // property_changed dafuer feuert - sie stand bis dahin in keiner Liste und
  // wurde nie gelesen.
  //
  // Interessant als moeglicher Standby-Nachweis: auf dem 43UK660H gibt es
  // keinen anderen (isVideoMute quittiert, power_mode_changed feuert nicht,
  // soft_ap steht beim Nachsehen schon wieder auf 1). Ob sie wirklich am
  // Ausschalten haengt, ist NICHT vermessen - im Log kam sie bei laufendem
  // Geraet. Deshalb wird sie zunaechst nur gelesen und beobachtet.
  'standby_light',
];

// Ab dieser Generation bringt der TV sein eigenes Cast-/AirPlay-Pairing mit;
// die App soll dann nicht mehr selbst ein Pairing nachbauen, sondern nur noch
// dorthin uebergeben.
export const LG_NATIVE_CAST_MIN_VERSION = 25;

// Ab hier existiert ueberhaupt ein natives Cast-Erlebnis (Screen Share/AirPlay).
export const LG_CAST_MIN_VERSION = 23;

// Zeitbudget fuer hcap-Aufrufe im AUSSCHALTPFAD.
//
// Der Standard von 5000 ms ist dort untragbar: in der Power-Off-Sequenz ruft die
// Firmware ueberhaupt keinen Callback mehr auf, jeder Aufruf laeuft also voll in
// den Timeout. Vier davon hintereinander (das alte rotateSoftAP) waren die
// gemessenen 18 s bis der Bildschirm dunkel wurde. Auf eine Bestaetigung wird
// hier ohnehin nicht gewartet - der TV geht gleich aus.
const SHUTDOWN_TIMEOUT_MS = 1500;

// Ruhezeit nach einem eigenen soft_ap-Schreibzugriff. Innerhalb dieser Spanne
// zaehlt ein Abfall auf 0 nicht als Power-Off - siehe watchSoftApDrop().
const SOFTAP_WRITE_QUIET_MS = 10000;

function hasHcap() {
  return typeof hcap !== 'undefined' && !!hcap;
}

function logErr(scope, f) {
  const msg = (f && (f.errorMessage || f.message)) || String(f);
  console.log('[Cast/LG] ' + scope + ' error: ' + msg);
}

// Wrappt eine hcap-Methode mit onSuccess/onFailure als Promise.
//
// Mit Timeout: manche Firmwares rufen bei unbekannten Keys/Methoden ueberhaupt
// keinen Callback auf. Ohne Timeout wuerde die Capability-Erkennung (Promise.all)
// dann nie fertig und die Cast-/Info-Seite ewig laden.
function call(ns, method, params, timeoutMs) {
  return new Promise((resolve, reject) => {
    if (!hasHcap() || !hcap[ns] || typeof hcap[ns][method] !== 'function') {
      reject(new Error('hcap.' + ns + '.' + method + ' missing'));
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('hcap.' + ns + '.' + method + ' timeout'));
    }, timeoutMs || 5000);
    const finish = (fn, arg) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn(arg);
    };
    try {
      hcap[ns][method](
        Object.assign({}, params, {
          onSuccess: function(s) {
            finish(resolve, s);
          },
          onFailure: function(f) {
            logErr(ns + '.' + method, f);
            finish(reject, f);
          },
        })
      );
    } catch (e) {
      logErr(ns + '.' + method, e);
      finish(reject, e);
    }
  });
}

export default class CastLG {
  // Cast-Feature laut Vorgabe nur WebOS >= 23. Die zugrundeliegenden HCAP-APIs
  // (launchApplication ab 1.20.1, checkout/cloning ab 1.19.0) sind zwar viel
  // aelter, aber das native Google-Cast-Erlebnis existiert erst ab webOS 23.
  //
  // Reihenfolge der Entscheidung:
  //  1. Nachgewiesener Cast-Einstieg auf dem Geraet -> unterstuetzt.
  //  2. Unzuverlaessige Versionsangabe (nur Chrome-Ableitung aus einer
  //     PCBR-UserAgent) -> NICHT unterstuetzt. Ein 43UK660H von 2018 meldet
  //     Chrome 120 und wuerde sonst als webOS 24 durchgehen.
  //  3. Sonst die Version.
  static isSupported() {
    try {
      const caps = CastLG._caps;
      if (!LG_CAST_STRICT_VERSION_GATE && caps && caps.detected && caps.castEntryId) return true;
      if (DeviceType.isWebOSVersionUnreliable) return false;
      return DeviceType.getWebOSMajor >= LG_CAST_MIN_VERSION;
    } catch (e) {
      return false;
    }
  }

  // Zeigt der TV sein Cast-Pairing selbst an? Harter Nachweis ist ein
  // gemeldeter Google-Cast-Einstieg ("Google Cast Guide"). AirPlay ist dafuer
  // NICHT Voraussetzung - das 43UK660H (webOS 25) meldet Google Cast, aber
  // keine AirPlay-App.
  static isNativeCastSupported() {
    try {
      const caps = CastLG._caps;
      if (caps && caps.detected && caps.googleCast) return true;
      if (caps && caps.detected) return false; // Erkennung durch, kein Cast da
      if (DeviceType.isWebOSVersionUnreliable) return false;
      return DeviceType.getWebOSMajor >= LG_NATIVE_CAST_MIN_VERSION;
    } catch (e) {
      return false;
    }
  }

  // =========================================================================
  // Capability-Erkennung
  // =========================================================================

  // Zuletzt ermittelte Faehigkeiten (synchron, ohne Geraeteabfrage).
  // detected=false bedeutet: detect() ist noch nicht durchgelaufen.
  static capabilities() {
    if (CastLG._caps) return CastLG._caps;
    CastLG._caps = {
      detected: false,
      webos: DeviceType.getWebOSMajor,
      catalog: [],
      apps: [],
      preloadedApps: [],
      castEntryId: null,
      castEntryCandidates: [],
      screenShareIds: [],
      screenShare: false,
      googleCast: false,
      airplay: false,
      properties: {},
      network: null,
      serviceCountry: null,
      networkQr: false,
      softApQr: false,
      // Hat ein SCHREIBZUGRIFF auf tv_name funktioniert? null = noch nicht
      // versucht. Das ist die eigentlich interessante Frage - siehe
      // _refreshSoftApQr().
      tvNameWritable: null,
      // Ergebnis der IDCAP-Parallelmessung (siehe helpers/Cast/Idcap.js).
      // null bis detect() durch ist. Aendert das Verhalten NICHT - HCAP bleibt
      // der aktive Pfad, bis IDCAP auf echter Hardware bestaetigt ist.
      idcap: null,
      idcapApps: [],
      // Was hcap fuers Cloning hergibt. Die Liste kommt aus der mitgelieferten
      // static/hcap.js und ist damit fest: requestCloning ANWENDEN, sonst
      // nichts. Ein Gegenstueck zum Auslesen gibt es nicht, und hcap.file kann
      // nur USB (downloadFileToUsb geht Netz -> USB, nicht umgekehrt).
      //
      // Steht hier trotzdem, damit die Info-Seite auf LG und Samsung dieselbe
      // Frage beantwortet, statt auf LG einfach eine Leerstelle zu lassen.
      cloneApi: CastLG.cloneApiNames(),
    };
    return CastLG._caps;
  }

  // Kann dieses Geraet einen eigenen SoftAP-QR tragen?
  //
  // ENTSCHEIDEND IST DER SCHREIBZUGRIFF, nicht der Lesezugriff. Auf einem
  // 43UK660H (webOS 25) lieferte getProperty fuer tv_name UND soft_ap null,
  // waehrend setProperty('tv_name') im selben Atemzug mit result:true
  // quittierte und die Firmware ein property_changed-Ereignis feuerte. Die
  // Property ist dort also schreib-, aber nicht lesbar - genau das, was die
  // IDCAP-Doku fuer soft_ap_password vor IDPN 304 beschreibt ("changed to
  // Read/Write from Write Only").
  //
  // Die alte Pruefung hing allein an der Lesbarkeit und hat diese Geraete
  // deshalb auf 'screenshare' geschickt: keine SSID, kein Passwort, kein QR -
  // nur der Hinweistext, obwohl der TV alles mitbringt.
  //
  // Reihenfolge:
  //  1. Ein gelungener Schreibzugriff ist der harte Nachweis.
  //  2. Sonst wie bisher die Lesbarkeit von tv_name bzw. soft_ap/soft_ap_ui.
  //  3. Ein FEHLGESCHLAGENER Schreibzugriff ueberstimmt alles - dann ist die
  //     SSID nicht setzbar und ein QR waere geraten.
  //
  // Der Aufrufer meldet das Ergebnis ueber noteTVNameWrite(); geschrieben wird
  // tv_name ohnehin bei jedem Start (cast/applyDeviceName), es kostet also
  // keinen zusaetzlichen Zugriff auf das Geraet.
  static _refreshSoftApQr() {
    const caps = CastLG._caps;
    if (!caps) return false;
    if (caps.tvNameWritable === false) {
      caps.softApQr = false;
      return false;
    }
    const props = caps.properties || {};
    const readable =
      props.tv_name !== null && props.tv_name !== undefined
        ? true
        : props.soft_ap !== null && props.soft_ap !== undefined;
    caps.softApQr = caps.tvNameWritable === true || readable;
    return caps.softApQr;
  }

  // Ergebnis eines tv_name-Schreibzugriffs melden. Liefert true, wenn sich die
  // Faehigkeit dadurch geaendert hat - dann muss die Strategie neu bestimmt
  // werden (siehe cast/applyDeviceName).
  static noteTVNameWrite(ok) {
    const caps = CastLG._caps;
    if (!caps) return false;
    const before = caps.softApQr;
    caps.tvNameWritable = !!ok;
    CastLG._refreshSoftApQr();
    if (caps.softApQr !== before) {
      console.log(
        '[Cast/LG] tv_name ist ' + (ok ? 'schreibbar' : 'NICHT schreibbar') +
        ' (lesbar: ' + (caps.properties && caps.properties.tv_name !== null) +
        ') - softApQr ' + before + ' -> ' + caps.softApQr
      );
      return true;
    }
    return false;
  }

  // Die Cloning-Funktionen von hcap.system, so wie sie das Geraet anbietet.
  static cloneApiNames() {
    const out = [];
    try {
      const sys = typeof hcap !== 'undefined' && hcap ? hcap.system : null;
      if (!sys) return out;
      for (const k in sys) {
        try {
          if (/clon/i.test(k) && typeof sys[k] === 'function') out.push(k);
        } catch (e) {
          /* manche Properties werfen beim Zugriff - dann eben ohne */
        }
      }
    } catch (e) {
      return out;
    }
    return out.sort();
  }

  // Fragt das Geraet EINMAL nach allem, was fuer die Cast-Strategie und die
  // Diagnose gebraucht wird. Wirft nie - fehlende APIs werden zu leeren Werten.
  static detect(force) {
    if (CastLG._detectPromise && !force) return CastLG._detectPromise;
    const caps = CastLG.capabilities();

    CastLG._detectPromise = Promise.all([
      CastLG.getApplicationList().catch(() => []),
      CastLG.getPreloadedApplicationList().catch(() => []),
      CastLG.getProperties(LG_CAST_PROPERTY_KEYS),
      CastLG.getNetworkInformation().catch(() => null),
      CastLG.getServiceCountry().catch(() => null),
      // Laeuft nur mit, um IDCAP auf echter Hardware auszumessen. Wirft nie und
      // beeinflusst keine der abgeleiteten Faehigkeiten.
      CastLG._probeIdcap().catch(() => null),
    ]).then((res) => {
      // getApplicationList() liefert einen KATALOG, nicht die installierten Apps.
      // Belegt auf einem 43UK660H: 55 Eintraege, davon nur 12 mit valid=true -
      // youtube.leanback.v4 und com.webos.app.commercial.chromecastguide stehen
      // mit valid=false drin. Reines Vorhandensein in der Liste sagt also
      // nichts; erst valid entscheidet, ob die App startbar ist.
      // valid !== false statt valid === true, damit Firmwares ohne dieses Feld
      // weiterhin alle Eintraege als nutzbar melden.
      caps.catalog = res[0] || [];
      caps.apps = caps.catalog.filter((a) => a.valid !== false);
      caps.preloadedApps = res[1] || [];
      caps.properties = res[2] || {};
      caps.network = res[3] || null;
      caps.serviceCountry = res[4] || null;
      caps.idcap = (res[5] && res[5].status) || null;
      caps.idcapApps = (res[5] && res[5].apps) || [];
      caps.webos = DeviceType.getWebOSMajor;

      // Nur installierte Apps zaehlen fuer die Faehigkeiten.
      const ids = {};
      caps.apps.forEach((a) => {
        if (a.id) ids[a.id] = a;
      });
      caps.castEntryCandidates = LG_CAST_ENTRY_CANDIDATES.filter((id) => !!ids[id]);
      caps.castEntryId = caps.castEntryCandidates.length ? caps.castEntryCandidates[0] : null;
      caps.screenShareIds = LG_SCREEN_SHARE_APP_IDS.filter((id) => !!ids[id]);
      caps.screenShare = caps.screenShareIds.length > 0;
      // Google Cast ist der harte Nachweis fuer das native Pairing; AirPlay ist
      // ein Zusatz und auf aelteren Geraeten nicht vorhanden.
      caps.googleCast = LG_GOOGLE_CAST_APP_IDS.some((id) => !!ids[id]);
      caps.airplay = LG_AIRPLAY_APP_IDS.some((id) => !!ids[id]);

      // Netzwerk-QR ist nur baubar, wenn der TV SSID (und moeglichst Passwort)
      // des verbundenen WLANs herausgibt. Bei LAN-Anbindung gibt es keine SSID.
      const ssid = caps.network && caps.network.ssid;
      caps.networkQr = !!(ssid && String(ssid).length);

      CastLG._refreshSoftApQr();

      caps.detected = true;
      CastLG._logAppList('CP-App', caps.catalog);
      console.log(
        '[Cast/LG] CP-Apps installiert: ' + caps.apps.length + ' von ' + caps.catalog.length +
        ' (' + caps.apps.map((a) => a.id).join(', ') + ')'
      );
      CastLG._logAppList('Preloaded-App', caps.preloadedApps);
      console.log(
        '[Cast/LG] Strategie-Basis: webos=' + caps.webos +
        ' castEntry=' + (caps.castEntryId || '-') +
        ' googleCast=' + caps.googleCast +
        ' airplay=' + caps.airplay +
        ' screenShare=' + (caps.screenShareIds.join(',') || '-') +
        ' networkQr=' + caps.networkQr +
        ' softApQr=' + caps.softApQr +
        ' tvName=' + ((caps.properties && caps.properties.tv_name) || '-') +
        ' idcap=' + ((caps.idcap && caps.idcap.working) || false)
      );
      return caps;
    }).catch((e) => {
      logErr('detect', e);
      caps.detected = true;
      return caps;
    });

    return CastLG._detectPromise;
  }

  // IDCAP-Parallelmessung. Beantwortet zwei Fragen, die sich nur auf echter
  // Hardware klaeren lassen:
  //  1. Antwortet der IDCAP-Daemon ueberhaupt? Davon haengt ab, ob die von LG
  //     empfohlene Migration weg von HCAP auf diesen Geraeten moeglich ist.
  //  2. Was meldet IDCAP pro App als token? notRequired/authSuccess/authNeeded
  //     sagt direkt, ob eine OTT-App noch eine Lizenzfreischaltung braucht -
  //     genau die Frage, die HCAP mit valid=false nicht beantworten kann.
  //
  // Beides ist reine Diagnose: nichts davon fliesst in Strategie, Support-Gate
  // oder App-Start ein. Wirft nie.
  static _probeIdcap() {
    return Idcap.probe()
      .then((status) => {
        if (!status || !status.working) return { status: status, apps: [] };
        return Idcap.getApplicationList()
          .then((apps) => {
            CastLG._logIdcapApps(apps);
            return { status: status, apps: apps };
          })
          .catch(() => ({ status: status, apps: [] }));
      })
      .catch(() => ({ status: Idcap.status(), apps: [] }));
  }

  // Eigenes Logging analog _logAppList: idcap.js schneidet seine Ausgaben
  // genau wie hcap.js bei 1024 Zeichen ab, die Rohliste ist im Log also nie
  // vollstaendig zu sehen.
  static _logIdcapApps(apps) {
    const items = apps || [];
    console.log('[Cast/IDCAP] App-Liste: ' + items.length + ' Eintraege');
    items.forEach((a, i) => {
      console.log(
        '[Cast/IDCAP] App ' + i + ': ' + (a.id || '-') + ' | ' + (a.name || '-') +
        ' installed=' + a.valid + ' token=' + (a.token || '-')
      );
    });
  }

  // Normalisiert die unterschiedlichen App-Listen-Schemata der WebOS-Versionen
  // auf {id, name, version, type} (analog helpers/Device/LG.getInstalledApps).
  //
  // WICHTIG: HCAP liefert die Liste NICHT als Array, sondern als Objekt mit
  // numerischen String-Keys (belegt durch get_preloaded_application_list):
  //   "list" : { "0" : {...}, "1" : {...}, "10" : {...} }
  // Ein .map() darauf wirft, die Liste bleibt leer und Fallbacks greifen nie.
  // Deshalb werden beide Formen unterstuetzt.
  static _normalizeAppList(raw) {
    const container =
      (raw && (raw.application_list || raw.applicationList || raw.applications || raw.list)) || raw;
    let list = [];
    if (Array.isArray(container)) {
      list = container;
    } else if (container && typeof container === 'object') {
      list = Object.keys(container)
        .filter((k) => /^\d+$/.test(k))
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => container[k]);
    }
    return list
      .map((entry) => {
        const a = entry || {};
        return {
          id: String(a.app_id || a.appId || a.id || ''),
          name: a.app_name || a.appName || a.name || a.title || '',
          version: a.version || a.app_version || '',
          type: a.app_type || a.appType || a.type,
          valid: a.valid,
          visible: a.visible,
        };
      })
      .filter((a) => a.id || a.name);
  }

  // Eigenes Logging der App-Listen.
  //
  // Notwendig, weil hcap.js seine Ausgaben hart bei 1024 Zeichen abschneidet
  // (siehe static/hcap.js: if(r.length>1024){t=r.substring(0,1024)}). Die
  // Rohantwort von get_application_list / get_preloaded_application_list ist im
  // Log deshalb NIE vollstaendig zu sehen - sie bricht immer an derselben
  // Stelle ab. Eine Zeile pro App bleibt auch in einem Remote-Log-Collector
  // lesbar und zeigt die App-Ids, die sich sonst nicht ermitteln lassen.
  static _logAppList(label, list) {
    const items = list || [];
    console.log('[Cast/LG] ' + label + '-Liste: ' + items.length + ' Eintraege');
    items.forEach((a, i) => {
      // valid/visible mitloggen: getApplicationList() liefert auf Hospitality
      // offenbar einen Katalog (55 Apps inkl. vieler Regional-Apps). Ob ein
      // Eintrag tatsaechlich installiert ist, entscheidet vermutlich valid -
      // ein nicht installierter Eintrag erklaert ein fehlschlagendes
      // launchApplication trotz korrekter App-Id.
      const flags =
        (a.valid === undefined ? '' : ' valid=' + a.valid) +
        (a.visible === undefined ? '' : ' visible=' + a.visible);
      console.log(
        '[Cast/LG] ' + label + ' ' + i + ': ' + (a.id || '-') + ' | ' + (a.name || '-') + flags
      );
    });
  }

  // Liest mehrere Properties und liefert {key: value|null}. Unbekannte Keys
  // werden zu null, damit die Diagnose zeigt, was das Geraet wirklich kennt.
  static getProperties(keys) {
    return Promise.all(
      (keys || []).map((key) =>
        CastLG.getProperty(key)
          .then((v) => [key, v === undefined ? null : v])
          .catch(() => [key, null])
      )
    ).then((pairs) => {
      const out = {};
      pairs.forEach((p) => {
        out[p[0]] = p[1];
      });
      return out;
    });
  }

  // ---- OTT-App-Start ------------------------------------------------------
  // LG kennt ZWEI App-Namespaces:
  //  - hcap.application.launchApplication({id}) (since v1.20.1) fuer CP-Apps mit
  //    String-ID (z.B. YouTube "youtube.leanback.v4", Amazon "amazon.html",
  //    Screen Share "com.webos.app.miracast"). Nur wenn Property asl_mode=="1".
  //  - hcap.preloadedApplication.launchPreloadedApplication({id}) (since v1.5.0)
  //    fuer vorinstallierte Apps mit NUMERISCHER ID. Premium-OTT wie Netflix/
  //    Disney+/Paramount+ liegen i.d.R. hier - die konkreten numerischen IDs
  //    sind pro Geraet/Region und muessen via getPreloadedApplicationList()
  //    ausgelesen werden (stehen NICHT in der CP-App-Tabelle).
  // launchApp routet automatisch: rein numerische ID -> preloaded, sonst CP.
  static launchApp(appId, opts) {
    const options = opts || {};
    if (CastLG._isNumericId(appId)) {
      return call('preloadedApplication', 'launchPreloadedApplication', { id: String(appId) }).then(() => appId);
    }
    const params = { id: appId };
    if (options.parameters) params.parameters = options.parameters;

    // Ist die App nicht installiert, ist launchApplication zwecklos. Dann direkt
    // in der Preloaded-Liste suchen - das erspart zwei Fehlversuche und eine
    // unnoetige Aenderung von asl_mode. Die Meldung unterscheidet dabei, ob die
    // Id unbekannt ist oder nur nicht installiert (valid=false) - letzteres ist
    // eine Frage der Provisionierung, nicht der App-Id.
    const caps = CastLG._caps;
    if (caps && caps.detected && !caps.apps.some((a) => a.id === appId)) {
      const inCatalog = (caps.catalog || []).some((a) => a.id === appId);
      const reason = inCatalog
        ? 'App ' + appId + ' steht im Katalog, ist aber nicht installiert (valid=false)'
        : 'App-Id ' + appId + ' steht nicht im App-Katalog des Geraets';
      return CastLG._launchPreloadedByName(options.name || appId, new Error(reason));
    }

    return call('application', 'launchApplication', params)
      .then(() => appId)
      .catch((err) => {
        // launchApplication setzt laut HCAP-Doku asl_mode == "1" voraus. Auf
        // frisch geklonten/zurueckgesetzten Geraeten steht das Flag auf 0 und
        // der Aufruf scheitert mit "launch_application() failed". Einmal setzen
        // und erneut versuchen.
        return CastLG.setProperty('asl_mode', 1)
          .catch(() => null)
          .then(() => call('application', 'launchApplication', params))
          .then(() => appId)
          .catch(() => CastLG._launchPreloadedByName(options.name || appId, err));
      });
  }

  // Fallback fuer den Fall, dass die App nicht als CP-App mit String-Id, sondern
  // vorinstalliert mit NUMERISCHER Id vorliegt. Diese Ids sind pro Geraet/Region
  // unterschiedlich und stehen in keiner Tabelle - sie muessen zur Laufzeit aus
  // getPreloadedApplicationList() aufgeloest werden (Abgleich ueber den Namen).
  static _launchPreloadedByName(name, originalError) {
    const needle = String(name || '').toLowerCase();
    if (!needle) return Promise.reject(originalError);
    return CastLG.getPreloadedApplicationList()
      .then((list) => {
        const hit = (list || []).filter((a) => {
          const hay = ((a.name || '') + ' ' + (a.id || '')).toLowerCase();
          return hay.indexOf(needle) !== -1;
        })[0];
        if (!hit || !hit.id) return Promise.reject(originalError);
        console.log('[Cast/LG] Fallback auf preloaded App ' + hit.id + ' (' + hit.name + ')');
        return call('preloadedApplication', 'launchPreloadedApplication', {
          id: String(hit.id),
        }).then(() => hit.id);
      })
      .catch(() => Promise.reject(originalError));
  }

  static _isNumericId(id) {
    return typeof id !== 'undefined' && id !== null && /^[0-9]+$/.test(String(id));
  }

  // Liste der vorinstallierten Apps (numerische IDs) - hier die premium-OTT
  // (Netflix/Disney+/Paramount+) zur Laufzeit auf ihre numerische ID mappen.
  // Beide Listen liefern normalisierte {id, name, version, type, valid, visible}.
  // Das Ergebnis-Schema unterscheidet sich je WebOS-Version (list /
  // application_list / applications), deshalb geht die Rohantwort durch
  // _normalizeAppList - sonst wuerde auf manchen Firmwares eine leere Liste
  // gemeldet, obwohl das Geraet Apps zurueckgibt.
  static getPreloadedApplicationList() {
    return call('preloadedApplication', 'getPreloadedApplicationList').then((s) =>
      CastLG._normalizeAppList(s)
    );
  }

  // Liste der installierten/verfuegbaren Apps (id/title/valid/visible).
  static getApplicationList() {
    return call('application', 'getApplicationList').then((s) => CastLG._normalizeAppList(s));
  }

  // Zustand einer App fuer die Install-Seite. Grundlage ist der App-Katalog:
  //   valid !== false        -> installed
  //   im Katalog, valid=false -> installable
  //   nicht im Katalog        -> missing (nicht installierbar)
  //
  // Ist die konfigurierte Id unbekannt, wird zusaetzlich per Name/Alias gesucht -
  // in Katalog UND Preloaded-Liste. Damit werden modell- bzw. regionsabhaengige
  // Ids gefunden, ohne sie vorher zu kennen; die gefundene Id kommt als
  // resolvedAppId zurueck und wird fuer die Installation benutzt.
  static getInstallState(appId, options) {
    const opts = options || {};
    return CastLG.detect().then(() => {
      const caps = CastLG.capabilities();
      const byId = appId ? (caps.catalog || []).filter((a) => a.id === appId)[0] : null;
      if (byId) return CastLG._stateFromEntry(byId);

      const inCatalog = CastLG._findByAlias(caps.catalog, opts);
      if (inCatalog) {
        const s = CastLG._stateFromEntry(inCatalog);
        return Object.assign(s, {
          resolvedAppId: inCatalog.id,
          detail: s.detail + ' - Id am Geraet: ' + inCatalog.id + ' (konfiguriert: ' + (appId || 'keine') + ')',
        });
      }
      const preloaded = CastLG._findByAlias(caps.preloadedApps, opts);
      if (preloaded) {
        return {
          state: 'installed',
          resolvedAppId: preloaded.id,
          detail: 'vorinstalliert, numerische Id ' + preloaded.id + ' (konfiguriert: ' + (appId || 'keine') + ')',
        };
      }
      return {
        state: 'missing',
        detail: 'weder Id noch Name im Katalog oder in der Preloaded-Liste - das Geraet bietet die App nicht an',
      };
    });
  }

  static _stateFromEntry(entry) {
    if (entry.valid !== false) {
      return {
        state: 'installed',
        detail: entry.visible === false ? 'installiert, nicht im Launcher' : 'installiert',
      };
    }
    return { state: 'installable', detail: 'im Katalog, valid=false' };
  }

  // Sucht einen Eintrag ueber Aliasse bzw. den Anzeigenamen - bester Treffer
  // nach Rang, Nebenvarianten (Kids, Settings, ...) zaehlen nicht. Verglichen
  // wird gegen Name UND Id, weil je Firmware nur eines von beiden
  // aussagekraeftig ist (siehe appMatch.js).
  static _findByAlias(list, opts) {
    return findAppMatch(list, opts);
  }

  // Service-Country des TV (hcap.system.getServiceCountry). Bestimmt, welche
  // CP-Apps LG dem Geraet ueberhaupt anbietet - eine falsch gesetzte Region
  // erklaert einen unvollstaendigen App-Katalog.
  // Das Feld heisst auf HCAP 1.24 countryCode ("DE"); die uebrigen Namen sind
  // defensive Varianten. Ohne diese Aufloesung landet das komplette
  // Antwortobjekt in der Diagnose statt des Laendercodes.
  static getServiceCountry() {
    return call('system', 'getServiceCountry').then((s) => {
      if (!s) return null;
      return s.countryCode || s.country || s.serviceCountry || s.value || null;
    });
  }

  // App-Installation/-Deinstallation (since v1.24.6). AppList = Array von IDs.
  static installApp(appId) {
    const list = Array.isArray(appId) ? appId : [appId];
    return call('application', 'installApplications', { AppList: list }).then(() => appId);
  }
  static removeApp(appId) {
    const list = Array.isArray(appId) ? appId : [appId];
    return call('application', 'removeApplications', { AppList: list });
  }

  // Startet die native Screen-Share-(Miracast-)App. Das ist auf LG der einzige
  // programmatisch anstossbare "Casting"-Weg; Google Cast/AirPlay-Pairing
  // laeuft ueber LGs eingebaute Loesung (+ Gateway), nicht ueber HCAP.
  static launchScreenShare() {
    return CastLG.detect().then((caps) => {
      const chain = caps.screenShareIds.length ? caps.screenShareIds : LG_SCREEN_SHARE_APP_IDS;
      return chain.reduce(
        (p, id) => p.catch(() => CastLG.launchApp(id)),
        Promise.reject(new Error('screen share: kein Kandidat probiert'))
      );
    });
  }

  // Uebergibt an den nativen Cast-/Pairing-Einstieg des TV (webOS >= 25).
  // Probiert die auf diesem Geraet vorhandenen Kandidaten der Reihe nach durch;
  // erst wenn keiner startet, wird abgelehnt.
  static launchCastEntry() {
    return CastLG.detect().then((caps) => {
      // Echte Cast-Einstiege zuerst, Screen Share als letzter Ausweg.
      const chain = caps.castEntryCandidates.concat(
        caps.screenShareIds.length ? caps.screenShareIds : LG_SCREEN_SHARE_APP_IDS
      );
      return chain.reduce(
        (p, id) => p.catch(() => CastLG.launchApp(id)),
        Promise.reject(new Error('cast entry: kein Kandidat probiert'))
      );
    });
  }

  static getCapability() {
    // Kein direktes HCAP-Pendant zu Samsungs WAS-Capability. Ueber
    // getApplicationList().valid liesse sich Installationsstatus ableiten.
    return null;
  }

  // ---- Netzwerk ------------------------------------------------------------
  // hcap.network.getNetworkInformation liefert u.a. ip_address, ssid und
  // wifi_pass_key des VERBUNDENEN Netzes (nicht des SoftAP). Damit laesst sich
  // ein WLAN-QR bauen, der das Handy in dasselbe Netz wie den TV bringt -
  // danach findet die native Cast-Discovery den TV selbst.
  static getNetworkInformation() {
    return call('network', 'getNetworkInformation').then((s) => {
      if (!s) return null;
      return {
        ip: s.ip_address || s.ipAddress || '',
        ssid: s.ssid || '',
        key: s.wifi_pass_key || s.wifiPassKey || '',
        securityType: s.security_type || s.securityType || '',
        mode: s.network_mode || s.mode || '',
        raw: s,
      };
    });
  }

  // Zugangsdaten des verbundenen WLANs fuer die QR-Erzeugung. Liefert
  // ssid:'' wenn der TV per LAN haengt oder keine SSID herausgibt.
  static getNetworkQrCredentials() {
    return CastLG.getNetworkInformation()
      .then((n) => ({
        ssid: (n && n.ssid) || '',
        key: (n && n.key) || '',
        security: n && n.key ? 'WPA' : 'nopass',
      }))
      .catch(() => ({ ssid: '', key: '', security: 'nopass' }));
  }

  // ---- Property (hcap.property.getProperty/setProperty, since v1.3.1) -----
  // timeoutMs ist optional: die Einstellungs-Abfrage (helpers/DeviceSettings)
  // geht ueber zwei Dutzend Properties und kann sich den Standard von 5 s je
  // unbekanntem Key nicht leisten.
  static getProperty(key, timeoutMs) {
    return call('property', 'getProperty', { key: key }, timeoutMs).then((s) => (s && typeof s.value !== 'undefined' ? s.value : s));
  }
  static setProperty(key, value, timeoutMs) {
    // Eigene soft_ap-Schreibzugriffe stempeln, damit der Beobachter unten sie
    // nicht fuer einen Power-Off haelt.
    if (key === 'soft_ap') CastLG._lastSoftApWrite = Date.now();
    return call('property', 'setProperty', { key: key, value: String(value) }, timeoutMs);
  }

  // Faellt soft_ap VON SELBST auf 0, war der TV aus.
  //
  // DER ANLASS: auf dem 43UK660H gibt es sonst keinen Standby-Nachweis mehr.
  // Die Sonde misst nichts (isVideoMute quittiert, power_mode_changed feuert
  // nicht), und beim Aufwachen NACHZUSEHEN kommt zu spaet - die Firmware faehrt
  // den Hotspot beim Einschalten selbst wieder hoch, soft_ap steht dann schon
  // auf 1. Im Log des Geraets stand folgerichtig aufraeumenOffen=false nach
  // einem echten Power-Zyklus: weder das SoftAP-Passwort wurde neu vergeben
  // noch die Gastdaten geloescht.
  //
  // Die Firmware feuert aber ein property_changed fuer soft_ap - im Log belegt.
  // Also wird der Abfall festgehalten, WENN er passiert, statt ihn spaeter
  // vergeblich zu suchen. Das Ereignis traegt keinen Wert, deshalb wird danach
  // gelesen.
  //
  // Unsere eigenen Schreibzugriffe (soft_ap=0 in _writeSoftAP) sind
  // ausgenommen: sie stempeln _lastSoftApWrite, und innerhalb des Zeitfensters
  // zaehlt ein Abfall nicht.
  // `onDrop` wird gerufen, SOBALD der Abfall gesehen wurde - also waehrend der
  // TV aus ist. Genau dort gehoert der Passwortwechsel hin: ein setProperty auf
  // soft_ap_password reicht, der Uebergang 0 -> 1 beim Einschalten macht ihn
  // von selbst wirksam. Kein Zyklus, keine Wartezeit fuer den Gast, und der
  // Hotspot wird nicht angefasst, waehrend jemand daran haengt.
  static watchSoftApDrop(onDrop) {
    if (typeof onDrop === 'function') CastLG._onSoftApDrop = onDrop;
    if (CastLG._softApWatching || typeof document === 'undefined') return false;
    CastLG._softApWatching = true;
    document.addEventListener('property_changed', (ev) => {
      try {
        if (!ev) return;
        // standby_light nur MITSCHREIBEN. Die Firmware feuert von sich aus
        // dafuer, und der Name legt nahe, dass sie am Ausschalten haengt -
        // belegt ist das nicht (im Log kam sie bei laufendem Geraet). Mit Wert
        // und Zeitpunkt im Log laesst sich beim naechsten Power-Zyklus
        // nachsehen, ob sie als Standby-Nachweis taugt.
        if (ev.key === 'standby_light') {
          CastLG.getProperty('standby_light')
            .then((v) => {
              console.log('[Cast/LG] standby_light geaendert auf: ' + JSON.stringify(v));
            })
            .catch(() => undefined);
          return;
        }
        if (ev.key !== 'soft_ap') return;
        const since = Date.now() - (CastLG._lastSoftApWrite || 0);
        if (since < SOFTAP_WRITE_QUIET_MS) return;
        CastLG.getProperty('soft_ap')
          .then((v) => {
            if (String(v == null ? '' : v).trim() !== '0') return;
            CastLG._softApWentDown = true;
            console.log('[Cast/LG] soft_ap ist von selbst auf 0 gefallen - der TV war aus');
            if (typeof CastLG._onSoftApDrop === 'function') {
              try {
                CastLG._onSoftApDrop();
              } catch (e) {
                /* der Melder darf den Beobachter nicht anhalten */
              }
            }
          })
          .catch(() => undefined);
      } catch (e) {
        /* ein Beobachter darf nichts anhalten */
      }
    });
    return true;
  }

  // Schreibt ein neues SoftAP-Passwort, OHNE den Hotspot zu zykeln.
  //
  // Wirksam wird es beim naechsten Uebergang soft_ap 0 -> 1 - und der passiert
  // beim Einschalten des TV ohnehin. Gedacht fuer den Standby: dort stoert der
  // Schreibzugriff niemanden, waehrend ein Zyklus im laufenden Betrieb jedes
  // verbundene Handy hinauswirft.
  //
  // Der Aufrufer muss den Schluessel als "geschrieben, aber noch nicht gueltig"
  // merken (writeStagedKey im cast-Store): bis zum Einschalten sendet der
  // Hotspot weiter mit dem alten.
  static stageSoftApKey(key) {
    return CastLG.setProperty('soft_ap_password', key, SHUTDOWN_TIMEOUT_MS)
      .then(() => {
        console.log('[Cast/LG] neues SoftAP-Passwort im Standby geschrieben, gilt ab dem Einschalten');
        return true;
      })
      .catch((f) => {
        logErr('soft_ap_password (Standby)', f);
        return false;
      });
  }

  /** Abfall seit dem letzten Aufruf gesehen? Holt die Antwort ab und loescht sie. */
  static takeSoftApDownMark() {
    const seen = !!CastLG._softApWentDown;
    CastLG._softApWentDown = false;
    return seen;
  }

  // Aktiviert die fuer Casting relevanten Plattform-Flags (best-effort):
  //  - dial: DIAL-Protokoll (Handy startet YouTube/Netflix auf dem TV)
  //  - wifi_screen_share: WiDi/Wi-Fi-Direct (Screen Share / Miracast)
  //  - asl_mode: CP-App-Modus, noetig fuer application.launchApplication
  // Google Cast/AirPlay selbst laesst sich hierueber NICHT ein-/ausschalten -
  // das ist Teil von LGs eingebauter Loesung (ab webOS 23).
  static enableCasting() {
    // asl_mode ist laut HCAP-Doku Voraussetzung fuer
    // application.launchApplication - ohne das schlaegt der Start des nativen
    // Cast-Einstiegs fehl. Auf dem 43UK660H laesst es sich nicht setzen
    // ("set_tv_property() failed"), dial und wifi_screen_share dagegen schon.
    const keys = ['dial', 'wifi_screen_share', 'asl_mode'];
    return Promise.all(
      keys.map((key) =>
        CastLG.setProperty(key, 1).then(
          () => key + '=ok',
          () => key + '=fehler'
        )
      )
    ).then((results) => {
      // Je Flag das Ergebnis, nicht nur ein Sammel-"true".
      //
      // Vorher meldete der Aufrufer "Cast-Flags gesetzt: true", obwohl im
      // selben Log zwei Zeilen darueber asl_mode mit set_tv_property() failed
      // quittiert hatte - die Sammelmeldung verdeckte genau den Fehlschlag, um
      // den es geht.
      console.log('[Cast/LG] Cast-Flags: ' + results.join(' '));
      return results;
    });
  }

  // ---- Google Cast / ToS: auf LG kein HCAP-Pendant ------------------------
  static gcInitialize() {
    return false;
  }
  static gcFinalize() {
    return false;
  }
  static gcGetStatus() {
    return null;
  }
  static gcAcceptTermsOfService() {
    return false;
  }
  static gcLaunchSettings() {
    // Ersatz: nativen Cast-/Pairing-Einstieg des TV oeffnen.
    CastLG.launchCastEntry().catch(() => {});
    return true;
  }

  // ---- SoftAP -------------------------------------------------------------
  // SSID und Passwort des SoftAP liegen NICHT in hcap.network.setSoftAP (das
  // kann nur channel/signalStrength/VLAN/mode/securityType), sondern sind
  // Properties (LG-Support-Auskunft 08/2026):
  //   tv_name           -> SSID des SoftAP (zugleich der Geraetename)
  //   soft_ap_password  -> Passwort, Default "lge12345", max. 64 Zeichen
  //
  // Das Passwort ist NUR SETZBAR: getProperty gibt es aus Sicherheitsgruenden
  // nicht heraus. Deshalb wird - wie auf Samsung - immer aktiv geschrieben und
  // exakt der geschriebene Wert zurueckgegeben; nur so passt der angezeigte QR
  // garantiert zur tatsaechlichen Broadcast-Konfiguration.
  //
  // Rueckgabe ist bewusst synchron (gleiche Signatur wie Tizen): die
  // setProperty-Aufrufe laufen best-effort im Hintergrund, der QR haengt an den
  // uebergebenen bzw. erzeugten Werten, nicht am Callback des TV.
  // Neues SoftAP-Passwort beim Gastwechsel. Siehe CastTizen.rotateSoftAP.
  //
  // Auf LG wird AUSDRUECKLICH nur das Passwort getauscht: die SSID ist hier die
  // Property tv_name und damit zugleich der Geraetename in Cast-/AirPlay-Listen.
  // Sie bei jedem Gastwechsel zu wuerfeln wuerde den TV staendig umbenennen.
  static rotateSoftAP(newKey) {
    const key = newKey || CastLG._softApPassword(true);
    // Schreiben allein reicht nicht: der laufende SoftAP behaelt sein altes
    // Passwort, bis er neu initialisiert wird. Siehe _cycleSoftAP.
    //
    // DIE REIHENFOLGE IST GEMESSEN UND DARF NICHT GEDREHT WERDEN: erst
    // schreiben, DANN der Zyklus. Genau das wurde einmal "korrigiert", weil in
    // der Doku zu network.setSoftAP steht, Konfiguration sei nur bei soft_ap=0
    // moeglich - dieser Satz gilt aber fuer jene Methode (Kanal, VLAN), nicht
    // fuer die Property soft_ap_password.
    //
    // Am 43UK660H im direkten Vergleich:
    //   Passwort -> soft_ap=0 -> soft_ap=1   Verbindung mit dem neuen Passwort OK
    //   soft_ap=0 -> Passwort -> soft_ap=1   Verbindung NICHT moeglich
    // In beiden Faellen quittiert die Firmware mit result:true und getProperty
    // liefert den neuen Wert. Bei abgeschaltetem Hotspot nimmt sie den
    // Schreibzugriff also an, uebernimmt ihn aber nicht in die Konfiguration,
    // die der Uebergang 0 -> 1 danach aktiviert.
    let written = false;
    return CastLG.setProperty('soft_ap_password', key)
      .then(() => {
        // Ab hier steht der Schluessel auf dem Geraet - auch wenn der Wechsel
        // soft_ap 0 -> 1 darunter noch scheitert. Der Aufrufer muss ihn dann
        // trotzdem merken, sonst zeigt die Cast-Seite ein anderes Passwort an,
        // als auf dem TV gilt.
        written = true;
        return CastLG._cycleSoftAP();
      })
      .then(() => {
        // Merker anpassen, damit ein spaeteres configureSoftAP() mit demselben
        // Passwort nicht noch einmal schreibt (und den SoftAP neu startet).
        //
        // Die SSID gehoert hier MIT HINEIN, sonst laeuft der Hotspot zweimal
        // hintereinander neu an. Am Geraet gemessen: rotateSoftAP hinterliess
        // ssid=null, configureSoftAP verglich das gegen '00001-5RVK', fand einen
        // Unterschied und schrieb die ganze Konfiguration erneut - soft_ap 0->1
        // zum zweiten Mal innerhalb einer halben Sekunde. Jeder dieser Wechsel
        // wirft jedes verbundene Handy aus dem Netz.
        //
        // Der Wert kommt aus dem letzten getProperty(tv_name), ist also der
        // Stand des GERAETS. Weicht die gewuenschte SSID davon ab, erkennt
        // configureSoftAP das weiterhin und schreibt - wie es soll.
        const caps = CastLG._caps;
        const knownSsid =
          (CastLG._applied && CastLG._applied.ssid) ||
          (caps && caps.properties && caps.properties.tv_name) ||
          null;
        CastLG._applied = { ssid: knownSsid, key: key };
        console.log('[Cast/LG] SoftAP-Passwort neu vergeben (SSID bleibt "' + (knownSsid || '-') + '")');
        return { ok: true, written: true, key: key };
      })
      .catch((f) => {
        logErr('rotateSoftAP', f);
        return { ok: false, written: written, key: key };
      });
  }

  // Schaltet soft_ap aus und wieder ein.
  //
  // Das ist der einzige dokumentierte Weg, eine geaenderte SoftAP-Konfiguration
  // ohne Reboot wirksam zu machen. HCAP-Doku zu network.setSoftAP:
  //   "Configuring can only be done when the soft_ap property is 0. The
  //    configuration changes will become effective at the next boot or when the
  //    soft_ap property is changed to 1."
  // und zur Property soft_ap selbst: "when soft_ap is 1, soft AP and VLAN are
  // initialized with the stored configuration".
  //
  // Ohne diesen Wechsel sendet der Access Point weiter mit den alten
  // Zugangsdaten: Anzeige und QR zeigen das neue Passwort, das Handy kommt aber
  // nicht ins Netz. Genau dieses Symptom war der Anlass fuer diese Funktion.
  //
  // soft_ap_ui muss dabei an sein, sonst betreibt der TV den SoftAP gar nicht.
  static _cycleSoftAP() {
    return CastLG.setProperty('soft_ap', 0)
      .catch((f) => {
        logErr('soft_ap=0', f);
      })
      .then(() => CastLG._enableSoftAP());
  }

  // Die Einschalt-Haelfte von _cycleSoftAP - der Uebergang von soft_ap 0 auf 1,
  // der die gespeicherte Konfiguration aktiviert. soft_ap_ui muss dabei an sein,
  // sonst betreibt der TV den SoftAP gar nicht.
  static _enableSoftAP() {
    return CastLG.setProperty('soft_ap_ui', 1)
      .catch((f) => {
        logErr('soft_ap_ui=1', f);
      })
      // soft_ap NICHT blind auf 1 setzen, wenn er schon 1 ist.
      //
      // Am Geraet beobachtet: nach mehrfachem Oeffnen der Cast-Seite fand die
      // YouTube-App den TV auf einmal nicht mehr, obwohl das Handy weiterhin am
      // SoftAP hing. Im Log steht der Grund - auf jedes set_property soft_ap=1
      // folgt ein property_changed, AUCH wenn der Wert vorher schon 1 war. Die
      // Firmware setzt den Hotspot also jedes Mal neu auf, und der Cast-Dienst
      // verliert dabei offenbar seine Bindung.
      //
      // Der Lesezugriff kostet einen Aufruf und spart den Neuaufbau. Gibt das
      // Geraet nichts heraus (null), wird wie bisher geschrieben - lieber ein
      // Hotspot, der neu anlaeuft, als einer, der aus bleibt.
      .then(() => CastLG.getProperty('soft_ap').catch(() => null))
      .then((v) => {
        if (String(v == null ? '' : v).trim() === '1') {
          console.log('[Cast/LG] soft_ap steht bereits auf 1 - nicht erneut gesetzt');
          return true;
        }
        return CastLG.setProperty('soft_ap', 1).then(() => true);
      })
      .catch((f) => {
        logErr('soft_ap=1', f);
        return false;
      });
  }

  // Steht soft_ap auf 0? Siehe Cast.softApIsDown - auf LG der Nachweis, dass
  // zwischendurch ein Power-Off lag. Nicht lesbar -> null, damit der Aufrufer
  // "keine Aussage" von "Hotspot laeuft" unterscheiden kann.
  static softApIsDown() {
    return CastLG.getProperty('soft_ap')
      .then((v) => {
        const value = String(v == null ? '' : v).trim();
        console.log('[Cast/LG] soft_ap gelesen: "' + value + '"');
        if (value === '') return null;
        return value === '0';
      })
      .catch((f) => {
        logErr('getProperty(soft_ap)', f);
        return null;
      });
  }

  // Die Ausschalt-Haelfte allein - fuer den Ausschaltpfad des TV.
  //
  // Das kostet EINEN hcap-Aufruf statt vier: rotateSoftAP kettet
  // soft_ap_password -> soft_ap=0 -> soft_ap_ui=1 -> soft_ap=1. Das neue
  // Passwort wird deshalb erst beim Aufwachen vergeben, wo der Uebergang
  // 0 -> 1 ohnehin faellig ist - laut Doku faellt soft_ap "in power-off
  // sequence" auf 0 zurueck, der Hotspot ist im Standby also unten und niemand
  // kann sich in der Zwischenzeit mit dem alten Passwort verbinden.
  static disableSoftAP() {
    return CastLG.setProperty('soft_ap', 0, SHUTDOWN_TIMEOUT_MS)
      .then(() => true)
      .catch((f) => {
        logErr('soft_ap=0 (Standby)', f);
        return false;
      });
  }

  static configureSoftAP(config) {
    const c = config || {};
    const caps = CastLG._caps;
    const known = (caps && caps.properties) || {};

    // SSID: Konfig -> zuletzt vom Geraet gelesener tv_name -> Notnagel.
    const ssid = c.ssid || known.tv_name || 'HotelTV';
    // Passwort: Konfig -> einmalig erzeugtes Laufzeit-Passwort. Nie leer, weil
    // ein offener SoftAP im Hotel nicht in Frage kommt und LG ohnehin einen
    // Default ("lge12345") setzen wuerde.
    const key =
      (typeof c.key === 'string' && c.key.length ? c.key : null) || CastLG._softApPassword();

    // Ab hier laeuft alles STRENG DER REIHE NACH und wird in _apply gemerkt,
    // damit verifySoftAP() darauf warten kann. Vorher liefen die Schreibzugriffe
    // parallel und fire-and-forget - dabei blieb der laufende SoftAP mit seiner
    // alten Konfiguration in der Luft (siehe _cycleSoftAP).
    //
    // Uebersprungen wird der Schreibzugriff, wenn sich seit dem letzten in
    // diesem App-Start nichts geaendert hat: soft_ap 0 -> 1 wirft jedes
    // verbundene Handy aus dem Netz, und der Gast oeffnet die Cast-Seite
    // durchaus mehrfach. Der Merker gilt nur zur Laufzeit - nach einem App-Start
    // ist unbekannt, was auf dem Geraet steht, also wird einmal geschrieben.
    const applied = CastLG._applied;
    if (!applied || applied.ssid !== ssid || applied.key !== key) {
      CastLG._applied = { ssid: ssid, key: key };
      CastLG._apply = CastLG._writeSoftAP(c, ssid, key);
    } else {
      // Nichts zu schreiben - aber einschalten, ohne vorher auszuschalten.
      // soft_ap faellt laut Doku bei jedem Power-Off und jedem Reboot auf 0
      // zurueck ("reset to 0 in power-off sequence"), der Hotspot waere nach dem
      // Einschalten des TV also unten, obwohl die Konfiguration noch steht.
      CastLG._apply = CastLG._enableSoftAP();
    }

    // Lokal merken, damit ein erneutes Oeffnen der Cast-Seite dieselben Daten
    // zeigt, auch bevor detect() die Properties neu gelesen hat.
    if (caps && caps.properties) caps.properties.tv_name = ssid;

    return { ssid: ssid, key: key, security: c.security || 'WPA', softApQr: true };
  }

  // Schreibt die SoftAP-Konfiguration in der von LG vorgeschriebenen Reihenfolge:
  // erst soft_ap aus, dann konfigurieren, dann wieder an.
  //
  // network.setSoftAP verlangt das ausdruecklich ("Configuring can only be done
  // when the soft_ap property is 0"), und der Wechsel zurueck auf 1 ist es, der
  // die gespeicherte Konfiguration - inklusive Passwort - aktiviert.
  //
  // Achtung tv_name: die Property ist laut Doku "also an SSID of SoftAP, which
  // is valid after rebooting the TV". Eine frisch gesetzte SSID kann also erst
  // nach einem Reboot gesendet werden, auch wenn getProperty sie sofort
  // zurueckliefert. verifySoftAP kann das nicht aufdecken - deshalb bleibt die
  // SSID ueber Neustarts hinweg stabil (siehe sessionSoftApSsid im cast-Store)
  // und wird nur beim Gastwechsel neu vergeben.
  static _writeSoftAP(c, ssid, key) {
    // Schritt fuer Schritt mitschreiben. Auf Firmwares, die diese Properties
    // nicht ZURUECKLESEN (43UK660H: getProperty liefert null), kann
    // verifySoftAP nichts bestaetigen - dann ist dieses Protokoll die einzige
    // Auskunft darueber, ob der QR echte Zugangsdaten zeigt oder geratene.
    const steps = [];
    const note = (name, ok) => {
      steps.push(name + '=' + (ok ? 'ok' : 'fehler'));
    };
    return CastLG.setProperty('soft_ap', 0)
      .then(() => note('soft_ap0', true))
      .catch((f) => {
        note('soft_ap0', false);
        logErr('soft_ap=0', f);
      })
      .then(() =>
        CastLG.setTVName(ssid).then(
          () => note('tv_name', true),
          (f) => {
            note('tv_name', false);
            logErr('tv_name', f);
          }
        )
      )
      .then(() =>
        CastLG.setProperty('soft_ap_password', key).then(
          () => note('passwort', true),
          (f) => {
            note('passwort', false);
            logErr('soft_ap_password', f);
          }
        )
      )
      .then(() => {
        // Versteckte SSID findet das Handy beim Scannen nicht. Explizit setzen,
        // damit der Zustand zum QR passt (der traegt H:true nur bei hidden).
        if (typeof c.hidden !== 'boolean') return null;
        return CastLG.setProperty('softap_ssid_hidden', c.hidden ? 1 : 0).catch((f) =>
          logErr('softap_ssid_hidden', f)
        );
      })
      .then(() => {
        // Netzwerk-/VLAN-Parameter (channel/VLAN/mode) - ohne SSID/Key. Muss
        // zwischen soft_ap=0 und soft_ap=1 liegen, sonst lehnt LG den Aufruf ab.
        if (
          !hasHcap() ||
          !hcap.network ||
          typeof hcap.network.setSoftAP !== 'function' ||
          typeof c.channel === 'undefined'
        ) {
          return null;
        }
        const params = {
          channel: typeof c.channel === 'number' ? c.channel : 0,
          signalStrength: typeof c.signalStrength === 'number' ? c.signalStrength : 0,
          vlanId: typeof c.vlanId === 'number' ? c.vlanId : 0,
          vlanIp: c.vlanIp,
          vlanSubnet: c.vlanSubnet,
          vlanGateway: c.vlanGateway,
        };
        if (c.mode) params.mode = c.mode;
        if (c.securityType) params.securityType = c.securityType;
        return call('network', 'setSoftAP', params).catch((f) => logErr('setSoftAP', f));
      })
      .then(() => CastLG._enableSoftAP())
      .then((up) => {
        note('einschalten', up);
        console.log('[Cast/LG] SoftAP geschrieben: ' + steps.join(' '));
        return up;
      });
  }

  // Erzeugt einmalig ein SoftAP-Passwort (ohne verwechselbare Zeichen) und
  // merkt es sich fuer die Laufzeit. Notwendig, weil die Firmware das gesetzte
  // Passwort nicht zurueckliefert - ohne diesen Merker koennte der zuvor
  // angezeigte QR nicht reproduziert werden.
  static _softApPassword(force) {
    if (CastLG._genKey && !force) return CastLG._genKey;
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let s = '';
    for (let i = 0; i < 8; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
    CastLG._genKey = s;
    return s;
  }

  // Liest die SSID nach dem Schreiben zurueck und vergleicht.
  //
  // Notwendig, weil configureSoftAP() aus Signaturgruenden synchron die
  // BEABSICHTIGTEN Werte liefert und die setProperty-Aufrufe fire-and-forget
  // sind. Lehnt die Firmware tv_name ab (unbekannte Property, Laengenlimit,
  // gesperrt im Hotelmodus), zeigte der QR sonst Zugangsdaten, die nicht zum
  // tatsaechlichen SoftAP gehoeren - der Gast kaeme nicht ins Netz und die
  // Ursache waere von aussen nicht zu sehen.
  //
  // Das Passwort wird mitgeprueft: soft_ap_password ist laut HCAP-Property-Liste
  // eine normale Read/Write-Property (IDCAP: "changed to Read/Write from Write
  // Only in IDPN304"). Auf aelteren Firmwares antwortet getProperty nicht -
  // deshalb sind SSID und Passwort getrennt dreiwertig: readable/keyReadable
  // trennen "abweichend" von "nicht pruefbar". Nur bei readable && !matched
  // gilt der gelesene Wert als der echte.
  //
  // Wartet zuerst auf die Schreibzugriffe aus configureSoftAP - ohne das laese
  // man moeglicherweise noch den Zustand von vorher.
  static verifySoftAP(expectedSsid, expectedKey) {
    const expected = String(expectedSsid == null ? '' : expectedSsid);
    const expectedK = String(expectedKey == null ? '' : expectedKey);
    const pending = CastLG._apply || Promise.resolve(null);
    return pending
      .catch(() => null)
      .then(() =>
        Promise.all([
          CastLG.getProperty('tv_name').catch((e) => ({ __err: e })),
          CastLG.getProperty('soft_ap_password').catch((e) => ({ __err: e })),
        ])
      )
      .then((res) => {
        const nameRes = res[0];
        const keyRes = res[1];
        const nameErr = nameRes && nameRes.__err;
        const v = nameErr ? '' : nameRes;
        const actual = v === null || typeof v === 'undefined' ? '' : String(v);
        const readable = actual.length > 0;
        const matched = readable && actual === expected;

        const key = keyRes && keyRes.__err ? '' : keyRes === null || typeof keyRes === 'undefined' ? '' : String(keyRes);
        const keyReadable = key.length > 0;
        const keyMatched = keyReadable && key === expectedK;

        // Cache mitziehen, damit ein spaeteres configureSoftAP() nicht wieder
        // vom veralteten Wunschwert ausgeht.
        const caps = CastLG._caps;
        if (caps && caps.properties) {
          if (readable) caps.properties.tv_name = actual;
          if (keyReadable) caps.properties.soft_ap_password = key;
        }
        if (readable && !matched) {
          console.log(
            '[Cast/LG] SoftAP-SSID weicht ab: gesetzt "' + expected + '", gelesen "' + actual + '"'
          );
        }
        if (keyReadable && !keyMatched) {
          console.log(
            '[Cast/LG] SoftAP-Passwort weicht ab: gesetzt "' + expectedK + '", gelesen "' + key + '"'
          );
        }
        return {
          ssid: actual,
          expected: expected,
          readable: readable,
          matched: matched,
          key: key,
          expectedKey: expectedK,
          keyReadable: keyReadable,
          keyMatched: keyMatched,
          detail: readable
            ? matched
              ? 'ok'
              : 'abweichend'
            : 'tv_name nicht lesbar' + (nameErr ? ' (' + ((nameErr.errorMessage || nameErr.message) || nameErr) + ')' : ''),
        };
      });
  }

  static getSoftAP() {
    return call('network', 'getSoftAP');
  }
  // TV-Name == SoftAP-SSID. Synchron aus dem letzten detect()-Ergebnis, damit
  // die Signatur zu Tizen passt; null bis detect() durch ist.
  static getTVName() {
    const caps = CastLG._caps;
    return (caps && caps.properties && caps.properties.tv_name) || null;
  }
  // Schreibt die SSID/den Geraetenamen - und merkt sich, OB das ging.
  //
  // Der Merker ist auf manchen Firmwares die einzige Auskunft ueber die
  // Property: getProperty('tv_name') liefert dort null, obwohl der
  // Schreibzugriff funktioniert. Siehe _refreshSoftApQr().
  static setTVName(name) {
    return CastLG.setProperty('tv_name', name).then(
      (r) => {
        CastLG.noteTVNameWrite(true);
        // Was wir geschrieben haben, ist ab jetzt der bekannte Stand - auch
        // wenn das Geraet ihn nicht zurueckliest.
        const caps = CastLG._caps;
        if (caps && caps.properties) caps.properties.tv_name = name;
        return r;
      },
      (f) => {
        CastLG.noteTVNameWrite(false);
        throw f;
      }
    );
  }
  static addSoftAPStateChangeListener() {
    return -1;
  }
  static removeSoftAPStateChangeListener() {
    return false;
  }

  // ---- Firmware-Update ----------------------------------------------------
  // hcap.system.startManualUpdate (since v1.24.2). Quelle ist der konfigurierte
  // Pro:Centric-Server (setProcentricServer) - KEINE frei uebergebene URL wie
  // bei Samsung. Fortschritt via getUpdateProgress, Abbruch via cancelUpdate.
  static updateFirmware(_fw, onProgress) {
    // Abschluss/Status kommt als DOM-Event "software_update_event_received";
    // zusaetzlich getUpdateProgress als Fallback pollen.
    if (onProgress && typeof document !== 'undefined') {
      const onEvt = function(param) {
        onProgress(param);
      };
      document.addEventListener('software_update_event_received', onEvt);
    }
    return call('system', 'startManualUpdate').then((r) => {
      if (onProgress) {
        const poll = setInterval(() => {
          call('system', 'getUpdateProgress')
            .then((p) => {
              onProgress(p);
              const done = p && (p.progress === 100 || p.done === true);
              if (done) clearInterval(poll);
            })
            .catch(() => clearInterval(poll));
        }, 3000);
      }
      return r;
    });
  }
  static cancelFirmwareUpdate() {
    return call('system', 'cancelUpdate');
  }
  static unsetFirmwareListener() {
    /* kein persistenter Listener; Polling wird beim Fertigwerden beendet */
  }

  // ---- Cloning / Config-Restore ------------------------------------------
  // hcap.system.requestCloning (since v1.19.0). XML-Cloning = identisch zum
  // USB-Cloning; erwartet einen XML-String (nicht einen .sclone-Dateipfad).
  // Nach Abschluss feuert das TV das Event "cloning_done_received"; danach
  // Reboot empfohlen. Ueber das Clone-XML lassen sich auch die vorinstallierten
  // Apps (LGService.xml) steuern.
  static restoreClone(xmlOrPath, onProgress) {
    // Abschluss meldet das TV als DOM-Event "cloning_done_received".
    if (onProgress && typeof document !== 'undefined') {
      document.addEventListener('cloning_done_received', function(param) {
        onProgress(param);
      });
    }
    // Direktes XML erkennen; sonst als URL laden und dann klonen.
    if (typeof xmlOrPath === 'string' && xmlOrPath.trim().charAt(0) === '<') {
      return call('system', 'requestCloning', { xml: xmlOrPath });
    }
    return fetch(xmlOrPath)
      .then((r) => r.text())
      .then((xml) => call('system', 'requestCloning', { xml: xml }));
  }
  static unsetCloneListener() {
    /* Abschluss kommt ueber das cloning_done_received-Event */
  }

  // ---- Checkout: Clear Credentials + Snapshot-Restore ---------------------
  // hcap.checkout.requestCheckout (since v1.19.0) loescht Cache/History/
  // persoenliche Daten und stellt den Snapshot wieder her. takeCheckoutSnapshot
  // muss vorher mindestens einmal gelaufen sein. Reboot danach empfohlen.
  static clearCredentials() {
    return call('checkout', 'requestCheckout')
      .then(() => true)
      .catch(() => false);
  }
  // LG kennt kein "beim naechsten Neustart loeschen": requestCheckout wirkt
  // SOFORT und stellt dabei den Snapshot wieder her. Ein "Scharfschalten"
  // waehrend des Gastaufenthalts wuerde die Sitzung also mitten im Betrieb
  // zuruecksetzen - deshalb hier bewusst ein No-op. Geloescht wird auf LG
  // ausschliesslich beim Ausschalten bzw. beim Check-out.
  static armClearCredentials() {
    return false;
  }
  // ACHTUNG: "erfolgreich" heisst hier NICHT "geloescht".
  //
  // requestCheckout setzt den TV auf den CHECKOUT-SNAPSHOT zurueck. Ist nie
  // einer genommen worden, gibt es nichts, worauf zurueckgesetzt werden koennte
  // - die Firmware quittiert den Aufruf trotzdem mit result:true. Genau so ist
  // es im Feld aufgetreten: die App meldete "Anmeldedaten entfernt", und der
  // naechste Gast landete in YouTube in der Benutzerauswahl des vorigen.
  //
  // takeCheckoutSnapshot() wird derzeit NIRGENDS gerufen - bewusst, denn der
  // Snapshot friert den Zustand ein, auf den zurueckgesetzt wird. Zum falschen
  // Zeitpunkt genommen, konserviert er die Anmeldung eines Gastes und stellt sie
  // danach bei JEDEM Checkout wieder her. Der richtige Zeitpunkt ist eine
  // betriebliche Entscheidung, keine technische.
  //
  // Bis das geklaert ist, sagt wenigstens das Log die Wahrheit.
  static clearCredentialsNow() {
    return call('checkout', 'requestCheckout').then((r) => {
      console.log(
        '[Cast/LG] requestCheckout quittiert: ' + JSON.stringify(r) +
        ' - OB dabei etwas geloescht wurde, ist damit NICHT gesagt:' +
        ' ohne vorherigen takeCheckoutSnapshot hat der TV keinen Stand,' +
        ' auf den er zuruecksetzen koennte (siehe Kommentar in Cast/LG.js)'
      );
      return r;
    });
  }
  // Das Gegenstueck zu clearCredentialsNow: haelt den Zustand fest, auf den
  // requestCheckout spaeter zurueckSETZT.
  //
  // WANN das laufen darf, entscheidet der Aufrufer und nicht diese Datei -
  // siehe cast/takeCheckoutSnapshot. Ein Snapshot zum falschen Zeitpunkt
  // konserviert die Anmeldung eines Gastes und stellt sie danach bei JEDEM
  // Checkout wieder her.
  static takeCheckoutSnapshot() {
    return call('checkout', 'takeCheckoutSnapshot').then((r) => {
      console.log('[Cast/LG] Checkout-Snapshot genommen: ' + JSON.stringify(r));
      CastLG._snapshotTaken = true;
      return r;
    });
  }

  // Liegt auf diesem TV ein Checkout-Snapshot? HCAP kennt keine Abfrage dafuer,
  // also traegt der Aufrufer den persistierten Merker hier ein (cast/init).
  static noteCheckoutSnapshot(taken) {
    CastLG._snapshotTaken = !!taken;
    return CastLG._snapshotTaken;
  }

  // Ist das Loeschen auf diesem Geraet NACHWEISLICH wirksam?
  //
  // Genau dann, wenn es einen Snapshot gibt - ohne ihn quittiert
  // requestCheckout zwar, hat aber nichts zurueckzusetzen.
  //
  // Absichtlich getrennt von hasClearCredentialsNow(): jenes beantwortet "kann
  // das Geraet ueberhaupt sofort loeschen" und steuert den Standby-Neustart;
  // ein false dort wuerde also einen Neustart ausloesen, der laut Kommentar in
  // store/modules/cast.js an einem Samsung schon einen TV totgelegt hat. Dieses
  // hier beantwortet nur, ob hinter einem "erledigt" ein Nachweis steht.
  // Wird der Checkout erst durch einen Neustart sichtbar? Auf LG: ja.
  //
  // Am Geraet beobachtet (43UK660H): nach der roten Taste waren die
  // YouTube-Konten noch da, nach einem Power Off/On dagegen weg - bei
  // demselben Aufruf. Die HCAP-Doku sagt es zu requestCheckout ebenfalls
  // ("Reboot danach empfohlen"): der Checkout raeumt, aber die laufende
  // Firmware zeigt den geraeumten Stand erst nach dem Neustart. Beim
  // Ausschalten liefert der Gast diesen Neustart beilaeufig mit, beim
  // Tastendruck nicht.
  //
  // Getrennt von hasClearCredentialsNow(): dieses hier loest KEINEN Neustart
  // aus, es sagt nur, dass einer noetig ist - gefragt wird der Gast.
  static clearTakesEffectOnReboot() {
    return true;
  }

  static isClearCredentialsVerified() {
    return !!CastLG._snapshotTaken;
  }

  // ---- Reboot (hcap.power.reboot) ----------------------------------------
  static reboot() {
    return call('power', 'reboot').catch(() => {});
  }

  // ---- High-Level ---------------------------------------------------------
  // LG braucht kein SI-Token/ToS wie Samsung; "prepare" ist hier ein No-op.
  static initToken() {
    return true;
  }
  // LG braucht kein SI-Token/ToS. "prepare" aktiviert stattdessen best-effort
  // die Casting-Flags (DIAL / Wi-Fi Screen Share / ASL) und ermittelt die
  // Faehigkeiten des Geraets fuer die Strategie-Auswahl.
  static prepareForCheckIn() {
    CastLG.enableCasting();
    CastLG.detect().catch(() => {});
    return true;
  }
  // Bei Check-out: Checkout-Reset (loescht Gastdaten + stellt Snapshot her).
  static finalizeForCheckOut() {
    CastLG.clearCredentials();
    return true;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/Cast/LG.js